import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  FileText, Plus, Pencil, Trash2, ExternalLink, Search, Shield,
  BookOpen, AlertTriangle, List, ClipboardList, Filter, X as XIcon,
  Calendar as CalendarIcon, CheckCircle2, Clock, XCircle, Building2, Globe,
  FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon map for fixed categories
const iconForCategoria = (icono) => {
  const m = {
    shield: Shield,
    book: BookOpen,
    'alert-triangle': AlertTriangle,
    'file-text': FileText,
    list: List,
    clipboard: ClipboardList,
  };
  return m[icono] || FileText;
};

const estadoBadge = (estado) => {
  if (estado === 'vigente') return { label: 'Vigente', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle2 };
  if (estado === 'por_vencer') return { label: 'Por Vencer', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Clock };
  if (estado === 'vencido') return { label: 'Vencido', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle };
  return null;
};

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d + 'T12:00:00').toLocaleDateString('es-CL'); } catch { return d; }
};

export const Repositorio = () => {
  const [categorias, setCategorias] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [gerencias, setGerencias] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [catFilter, setCatFilter] = useState('all'); // categoria id or 'all'
  const [gerenciaFilter, setGerenciaFilter] = useState('all'); // gerencia id or 'all' or 'transversales'
  const [estadoFilter, setEstadoFilter] = useState('all'); // vigente/por_vencer/vencido
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  // Dialogs
  const [docDialog, setDocDialog] = useState({ open: false, editing: null });
  const [catConfigDialog, setCatConfigDialog] = useState({ open: false, cat: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const [docForm, setDocForm] = useState({
    categoria_id: '', nombre: '', version: '1.0', url: '', descripcion: '',
    codigo: '', gerencia_id: '', fecha_vigencia: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [catsRes, docsRes, gersRes] = await Promise.all([
        axios.get(`${API}/repositorio/categorias`),
        axios.get(`${API}/repositorio/documentos`),
        axios.get(`${API}/gerencias`),
      ]);
      setCategorias(catsRes.data || []);
      setDocumentos(docsRes.data || []);
      setGerencias(gersRes.data || []);
    } catch {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const categoriasTransversales = categorias.filter(c => c.tipo === 'transversal');
  const categoriasPorGerencia = categorias.filter(c => c.tipo === 'gerencia');

  const filteredDocs = useMemo(() => {
    const s = search.trim().toLowerCase();
    return documentos.filter(d => {
      if (catFilter !== 'all' && d.categoria_id !== catFilter) return false;
      if (gerenciaFilter === 'transversales' && d.categoria_tipo !== 'transversal') return false;
      if (gerenciaFilter !== 'all' && gerenciaFilter !== 'transversales' && d.gerencia_id !== gerenciaFilter) return false;
      if (estadoFilter !== 'all' && d.estado_vigencia !== estadoFilter) return false;
      if (s) {
        const hay = [d.nombre, d.codigo, d.descripcion, d.categoria_nombre, d.gerencia_nombre, d.version]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [documentos, catFilter, gerenciaFilter, estadoFilter, search]);

  // reset page when filters change
  useEffect(() => { setPage(1); }, [catFilter, gerenciaFilter, estadoFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filteredDocs.length / PAGE_SIZE));
  const pagedDocs = filteredDocs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreateDoc = (catId = '') => {
    setDocForm({
      categoria_id: catId, nombre: '', version: '1.0', url: '', descripcion: '',
      codigo: '', gerencia_id: '', fecha_vigencia: '',
    });
    setDocDialog({ open: true, editing: null });
  };

  const openEditDoc = (doc) => {
    setDocForm({
      categoria_id: doc.categoria_id,
      nombre: doc.nombre || '',
      version: doc.version || '1.0',
      url: doc.url || '',
      descripcion: doc.descripcion || '',
      codigo: doc.codigo || '',
      gerencia_id: doc.gerencia_id || '',
      fecha_vigencia: doc.fecha_vigencia || '',
    });
    setDocDialog({ open: true, editing: doc });
  };

  const activeCat = useMemo(
    () => categorias.find(c => c.id === docForm.categoria_id),
    [categorias, docForm.categoria_id]
  );

  const saveDoc = async () => {
    try {
      if (!docForm.categoria_id) { toast.error('Selecciona una categoria'); return; }
      if (!docForm.nombre.trim()) { toast.error('Nombre requerido'); return; }
      if (!docForm.url.trim()) { toast.error('Link requerido'); return; }
      const cat = categorias.find(c => c.id === docForm.categoria_id);
      if (cat?.tipo === 'gerencia' && !docForm.gerencia_id) { toast.error('Selecciona una gerencia'); return; }
      if (cat?.requires_codigo && !docForm.codigo?.trim()) { toast.error('Codigo requerido'); return; }
      if (cat?.requires_vigencia && !docForm.fecha_vigencia) { toast.error('Fecha de vigencia requerida'); return; }

      const payload = { ...docForm };
      if (!payload.gerencia_id) delete payload.gerencia_id;
      if (!payload.fecha_vigencia) payload.fecha_vigencia = null;

      if (docDialog.editing) {
        await axios.put(`${API}/repositorio/documentos/${docDialog.editing.id}`, payload);
        toast.success('Documento actualizado');
      } else {
        await axios.post(`${API}/repositorio/documentos`, payload);
        toast.success('Documento creado');
      }
      setDocDialog({ open: false, editing: null });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/repositorio/documentos/${deleteDialog.id}`);
      toast.success('Documento eliminado');
      setDeleteDialog({ open: false, id: null });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const saveCatConfig = async () => {
    try {
      const c = catConfigDialog.cat;
      await axios.put(`${API}/repositorio/categorias/${c.id}`, {
        nombre: c.nombre,
        tipo: c.tipo,
        requires_codigo: c.requires_codigo,
        requires_vigencia: c.requires_vigencia,
      });
      toast.success('Categoria actualizada');
      setCatConfigDialog({ open: false, cat: null });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  // counts per category
  const countByCat = useMemo(() => {
    const map = {};
    documentos.forEach(d => { map[d.categoria_id] = (map[d.categoria_id] || 0) + 1; });
    return map;
  }, [documentos]);

  // counts per estado (for the current filters except estado)
  const estadoCounts = useMemo(() => {
    const s = search.trim().toLowerCase();
    return documentos.reduce((acc, d) => {
      if (catFilter !== 'all' && d.categoria_id !== catFilter) return acc;
      if (gerenciaFilter === 'transversales' && d.categoria_tipo !== 'transversal') return acc;
      if (gerenciaFilter !== 'all' && gerenciaFilter !== 'transversales' && d.gerencia_id !== gerenciaFilter) return acc;
      if (s) {
        const hay = [d.nombre, d.codigo, d.descripcion, d.categoria_nombre, d.gerencia_nombre].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return acc;
      }
      if (d.estado_vigencia) acc[d.estado_vigencia] = (acc[d.estado_vigencia] || 0) + 1;
      return acc;
    }, {});
  }, [documentos, catFilter, gerenciaFilter, search]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="repositorio-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Control Documental</h1>
          <p className="text-sm text-slate-500">Gestion de documentos transversales y por gerencia</p>
        </div>
        <Button onClick={() => openCreateDoc()} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="nuevo-documento-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Documento
        </Button>
      </div>

      {/* Categorias en cards clickeables (tabs) */}
      <div className="space-y-3 mb-5">
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Globe className="w-3 h-3" /> Transversales (todos los trabajadores)
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <CatCard
              selected={catFilter === 'all'}
              onClick={() => setCatFilter('all')}
              nombre="Todos" icon={FolderOpen}
              color="#64748b"
              count={documentos.length}
            />
            {categoriasTransversales.map(c => (
              <CatCard key={c.id}
                selected={catFilter === c.id}
                onClick={() => setCatFilter(c.id)}
                onConfig={() => setCatConfigDialog({ open: true, cat: { ...c } })}
                nombre={c.nombre} icon={iconForCategoria(c.icono)}
                color="#0891b2"
                count={countByCat[c.id] || 0}
              />
            ))}
          </div>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
            <Building2 className="w-3 h-3" /> Por Gerencia
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {categoriasPorGerencia.map(c => (
              <CatCard key={c.id}
                selected={catFilter === c.id}
                onClick={() => setCatFilter(c.id)}
                onConfig={() => setCatConfigDialog({ open: true, cat: { ...c } })}
                nombre={c.nombre} icon={iconForCategoria(c.icono)}
                color="#ea580c"
                count={countByCat[c.id] || 0}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <Card className="border-0 shadow-sm mb-4">
        <CardContent className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
            <div className="md:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input
                placeholder="Buscar por nombre, codigo, descripcion, gerencia..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="repositorio-search"
              />
            </div>
            <div className="md:col-span-3">
              <Select value={gerenciaFilter} onValueChange={setGerenciaFilter}>
                <SelectTrigger className="h-9" data-testid="filter-gerencia">
                  <Building2 className="w-3.5 h-3.5 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las gerencias</SelectItem>
                  <SelectItem value="transversales">Solo transversales</SelectItem>
                  {gerencias.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger className="h-9" data-testid="filter-estado">
                  <Filter className="w-3.5 h-3.5 mr-1" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="vigente">Vigente {estadoCounts.vigente ? `(${estadoCounts.vigente})` : ''}</SelectItem>
                  <SelectItem value="por_vencer">Por Vencer {estadoCounts.por_vencer ? `(${estadoCounts.por_vencer})` : ''}</SelectItem>
                  <SelectItem value="vencido">Vencido {estadoCounts.vencido ? `(${estadoCounts.vencido})` : ''}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1 flex justify-end">
              {(catFilter !== 'all' || gerenciaFilter !== 'all' || estadoFilter !== 'all' || search) && (
                <Button
                  size="sm" variant="ghost"
                  onClick={() => { setCatFilter('all'); setGerenciaFilter('all'); setEstadoFilter('all'); setSearch(''); }}
                  data-testid="clear-filters"
                  title="Limpiar filtros"
                >
                  <XIcon className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de documentos */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-700">
              Documentos ({filteredDocs.length})
            </p>
            {loading && <span className="text-xs text-slate-400">Cargando...</span>}
          </div>
          {filteredDocs.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              No hay documentos con los filtros actuales
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-500 text-[11px] uppercase tracking-wider w-[100px]">Codigo</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-500 text-[11px] uppercase tracking-wider">Documento</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-500 text-[11px] uppercase tracking-wider">Categoria</th>
                    <th className="text-left px-4 py-2.5 font-medium text-slate-500 text-[11px] uppercase tracking-wider">Gerencia</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-500 text-[11px] uppercase tracking-wider">Vigencia</th>
                    <th className="text-center px-4 py-2.5 font-medium text-slate-500 text-[11px] uppercase tracking-wider w-[80px]">v</th>
                    <th className="text-right px-4 py-2.5 font-medium text-slate-500 text-[11px] uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedDocs.map(d => {
                    const Icon = iconForCategoria(categorias.find(c => c.id === d.categoria_id)?.icono);
                    const badge = estadoBadge(d.estado_vigencia);
                    return (
                      <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`doc-row-${d.id}`}>
                        <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{d.codigo || '-'}</td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-start gap-2">
                            <Icon className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium text-slate-800 truncate">{d.nombre}</p>
                              {d.descripcion && <p className="text-[11px] text-slate-400 line-clamp-1">{d.descripcion}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${d.categoria_tipo === 'transversal' ? 'bg-cyan-50 text-cyan-700' : 'bg-orange-50 text-orange-700'}`}>
                            {d.categoria_nombre}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-slate-600">{d.gerencia_nombre || <span className="text-slate-300">Transversal</span>}</td>
                        <td className="px-4 py-2.5 text-center">
                          {d.fecha_vigencia ? (
                            <div className="flex flex-col items-center gap-0.5">
                              <span className="text-[11px] text-slate-500">{fmtDate(d.fecha_vigencia)}</span>
                              {badge && (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border ${badge.cls}`}>
                                  <badge.Icon className="w-2.5 h-2.5" /> {badge.label}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-block bg-slate-100 text-slate-600 text-[10px] font-mono px-1.5 py-0.5 rounded">v{d.version}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <a href={d.url} target="_blank" rel="noopener noreferrer"
                              className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                              data-testid={`open-doc-${d.id}`}
                              title="Abrir documento">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <button
                              onClick={() => openEditDoc(d)}
                              className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                              data-testid={`edit-doc-${d.id}`}
                              title="Editar"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => setDeleteDialog({ open: true, id: d.id })}
                              className="p-1.5 rounded hover:bg-red-50 text-red-600"
                              data-testid={`delete-doc-${d.id}`}
                              title="Eliminar"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {filteredDocs.length > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-xs">
              <span className="text-slate-500">
                Mostrando {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredDocs.length)} de {filteredDocs.length}
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="repo-prev-page">Anterior</button>
                <span className="px-2 font-semibold text-slate-700">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-2 py-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="repo-next-page">Siguiente</button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal crear/editar documento */}
      <Dialog open={docDialog.open} onOpenChange={(v) => !v && setDocDialog({ open: false, editing: null })}>
        <DialogContent className="max-w-xl" data-testid="doc-dialog">
          <DialogHeader>
            <DialogTitle>{docDialog.editing ? 'Editar Documento' : 'Nuevo Documento'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Categoria *</Label>
              <Select value={docForm.categoria_id} onValueChange={(v) => setDocForm(prev => ({ ...prev, categoria_id: v }))}>
                <SelectTrigger data-testid="doc-categoria-select"><SelectValue placeholder="Selecciona categoria" /></SelectTrigger>
                <SelectContent>
                  {categorias.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.tipo === 'transversal' ? '🌐 ' : '🏢 '}{c.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {activeCat?.tipo === 'gerencia' && (
              <div className="col-span-2">
                <Label className="text-xs">Gerencia *</Label>
                <Select value={docForm.gerencia_id} onValueChange={(v) => setDocForm(prev => ({ ...prev, gerencia_id: v }))}>
                  <SelectTrigger data-testid="doc-gerencia-select"><SelectValue placeholder="Selecciona gerencia" /></SelectTrigger>
                  <SelectContent>
                    {gerencias.map(g => <SelectItem key={g.id} value={g.id}>{g.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {activeCat?.requires_codigo && (
              <div>
                <Label className="text-xs">Codigo *</Label>
                <Input value={docForm.codigo} onChange={(e) => setDocForm(prev => ({ ...prev, codigo: e.target.value }))} placeholder="Ej: PRO-001" data-testid="doc-codigo" />
              </div>
            )}
            <div className={activeCat?.requires_codigo ? 'col-span-1' : 'col-span-2'}>
              <Label className="text-xs">Nombre *</Label>
              <Input value={docForm.nombre} onChange={(e) => setDocForm(prev => ({ ...prev, nombre: e.target.value }))} placeholder="Nombre del documento" data-testid="doc-nombre" />
            </div>

            {activeCat?.requires_vigencia && (
              <div>
                <Label className="text-xs">Fecha de Vigencia *</Label>
                <Input type="date" value={docForm.fecha_vigencia || ''} onChange={(e) => setDocForm(prev => ({ ...prev, fecha_vigencia: e.target.value }))} data-testid="doc-vigencia" />
              </div>
            )}
            <div>
              <Label className="text-xs">Version</Label>
              <Input value={docForm.version} onChange={(e) => setDocForm(prev => ({ ...prev, version: e.target.value }))} placeholder="1.0" />
            </div>

            <div className="col-span-2">
              <Label className="text-xs">Link *</Label>
              <Input value={docForm.url} onChange={(e) => setDocForm(prev => ({ ...prev, url: e.target.value }))} placeholder="https://..." data-testid="doc-url" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Descripcion</Label>
              <Input value={docForm.descripcion} onChange={(e) => setDocForm(prev => ({ ...prev, descripcion: e.target.value }))} placeholder="(opcional)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocDialog({ open: false, editing: null })}>Cancelar</Button>
            <Button onClick={saveDoc} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-doc-btn">
              {docDialog.editing ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal configurar categoria (solo requires_codigo/vigencia) */}
      <Dialog open={catConfigDialog.open} onOpenChange={(v) => !v && setCatConfigDialog({ open: false, cat: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar {catConfigDialog.cat?.nombre}</DialogTitle>
          </DialogHeader>
          {catConfigDialog.cat && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">Tipo: <strong>{catConfigDialog.cat.tipo === 'transversal' ? 'Transversal' : 'Por Gerencia'}</strong></div>
              <label className="flex items-center justify-between gap-2 text-sm cursor-pointer px-3 py-2 border rounded-lg">
                <span>Requiere codigo</span>
                <input type="checkbox" checked={!!catConfigDialog.cat.requires_codigo}
                  onChange={(e) => setCatConfigDialog(prev => ({ ...prev, cat: { ...prev.cat, requires_codigo: e.target.checked } }))}
                />
              </label>
              <label className="flex items-center justify-between gap-2 text-sm cursor-pointer px-3 py-2 border rounded-lg">
                <span>Requiere fecha de vigencia</span>
                <input type="checkbox" checked={!!catConfigDialog.cat.requires_vigencia}
                  onChange={(e) => setCatConfigDialog(prev => ({ ...prev, cat: { ...prev.cat, requires_vigencia: e.target.checked } }))}
                />
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatConfigDialog({ open: false, cat: null })}>Cancelar</Button>
            <Button onClick={saveCatConfig} className="bg-teal-600 hover:bg-teal-700 text-white">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, id: null })}
        onConfirm={confirmDelete}
        title="Eliminar Documento"
        description="¿Estas seguro de eliminar este documento? Esta accion no se puede deshacer."
      />
    </div>
  );
};

function CatCard({ nombre, icon: Icon, color, count, selected, onClick, onConfig }) {
  return (
    <div className="relative">
      <button
        onClick={onClick}
        className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-left ${selected ? 'border-teal-500 bg-teal-50 shadow-sm' : 'border-slate-100 bg-white hover:border-slate-200 hover:shadow-sm'}`}
        data-testid={`cat-card-${nombre}`}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-slate-800 leading-tight line-clamp-2">{nombre}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{count} doc{count === 1 ? '' : 's'}</p>
        </div>
      </button>
      {onConfig && (
        <button
          onClick={(e) => { e.stopPropagation(); onConfig(); }}
          className="absolute top-1.5 right-1.5 w-5 h-5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 flex items-center justify-center"
          title="Configurar"
        >
          <Pencil className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
}

export default Repositorio;
